import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import html2canvas from 'html2canvas';
import {
  Share as ShareIcon,
  IosShare as IosShareIcon
} from '@mui/icons-material';
import { CircularProgress } from '@mui/material';

/**
 * ServicesFAB Component
 * Floating Action Button for sharing Community Services with screenshot
 * Single-purpose button (no submenu, no auth logic)
 */
const ServicesFAB = ({
  onShare,
  centerName,
  theme
}) => {
  const [shouldPulse, setShouldPulse] = useState(false);
  const [isCapturing, setIsCapturing] = useState(false);

  // Check if we're on mobile and iOS
  const isMobile = window.innerWidth <= 768;
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

  // Add pulse animation every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setShouldPulse(true);
      setTimeout(() => setShouldPulse(false), 600);
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  // Handle share with callback to parent
  const handleShare = async () => {
    setIsCapturing(true);
    try {
      await onShare();
    } catch (err) {
      console.error('[ServicesFAB] Share failed:', err);
    } finally {
      setIsCapturing(false);
    }
  };

  // Animation variants
  const fabVariants = {
    pulse: {
      scale: [1, 1.15, 1],
      boxShadow: [
        '0 4px 12px rgba(0,0,0,0.15)',
        '0 8px 25px rgba(0,0,0,0.25)',
        '0 4px 12px rgba(0,0,0,0.15)'
      ],
      transition: {
        duration: 0.6,
        ease: "easeInOut"
      }
    }
  };

  // Styles
  const styles = {
    container: {
      position: 'fixed',
      bottom: isMobile ? '90px' : '90px',
      right: '24px',
      zIndex: 9999
    },
    fab: {
      width: '64px',
      height: '64px',
      borderRadius: '50%',
      backgroundColor: theme?.colors?.primary || '#10B981', // Green for share
      color: 'white',
      border: 'none',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '2px',
      cursor: isCapturing ? 'wait' : 'pointer',
      outline: 'none',
      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
      opacity: isCapturing ? 0.7 : 1,
      padding: '8px'
    },
    icon: {
      fontSize: '22px',
      lineHeight: '1'
    },
    label: {
      fontSize: '9px',
      fontWeight: '600',
      textTransform: 'uppercase',
      letterSpacing: '0.3px',
      lineHeight: '1',
      userSelect: 'none'
    }
  };

  return (
    <div style={styles.container}>
      <motion.button
        style={styles.fab}
        onClick={handleShare}
        disabled={isCapturing}
        variants={fabVariants}
        animate={shouldPulse && !isCapturing ? "pulse" : {}}
        whileHover={!isCapturing ? {
          scale: 1.1,
          boxShadow: '0 8px 25px rgba(0,0,0,0.25)',
          filter: 'brightness(1.1)'
        } : {}}
        whileTap={!isCapturing ? { scale: 0.95 } : {}}
        aria-label="Share services"
        title={isCapturing ? "Preparing share..." : "Share Services"}
      >
        {isCapturing ? (
          <>
            <CircularProgress size={20} sx={{ color: 'white' }} />
            <span style={styles.label}>Wait</span>
          </>
        ) : (
          <>
            {isIOS ? (
              <IosShareIcon style={styles.icon} />
            ) : (
              <ShareIcon style={styles.icon} />
            )}
            <span style={styles.label}>Share</span>
          </>
        )}
      </motion.button>
    </div>
  );
};

export default ServicesFAB;
